import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Plus } from "lucide-react";
import { useToast } from "../hooks/useToast";
import { getIntervalOptions, addIntervalOption } from "../api/moniter";

/* Interval dropdowns for Work Monitor.
 *
 * Each dropdown keeps its own built-in choices (5 / 10 / 15 / 30 …) and adds
 * everything /monitor_interval_options reports for the branch: the intervals
 * its schedules already use, plus any custom value saved on the branch's
 * activity setting (activitysetting.intervalOptions). "Add interval" writes
 * there, so the new value is permanent and shows up for every other user.
 *
 * The branch list is fetched ONCE per page load and shared by every instance
 * on screen (there are up to three in a single form), which is what the tiny
 * module-level store below is for.
 */

type Listener = (values: number[]) => void;

let cache: number[] | null = null;
let inflight: Promise<unknown> | null = null;
const listeners = new Set<Listener>();

const publish = (values: number[]) => {
  cache = values;
  listeners.forEach((listener) => listener(values));
};

const ensureLoaded = () => {
  // Left uncached on failure so the next mount retries rather than showing an
  // empty list forever.
  if (cache !== null || inflight) return;
  inflight = getIntervalOptions()
    .then(publish)
    .catch(() => undefined)
    .finally(() => {
      inflight = null;
    });
};

/** The intervals this branch already uses, shared across every IntervalSelect. */
export const useCustomIntervals = (): number[] => {
  const [values, setValues] = useState<number[]>(cache ?? []);
  useEffect(() => {
    listeners.add(setValues);
    ensureLoaded();
    if (cache) setValues(cache);
    return () => {
      listeners.delete(setValues);
    };
  }, []);
  return values;
};

// Radix needs a non-empty string value; this one is never stored on the form.
const ADD_VALUE = "__add_custom_interval__";

interface IntervalSelectProps {
  /** Selected minutes. Numbers and numeric strings both work. */
  value?: string | number | null;
  /** Called with the chosen minutes as a string, e.g. "45". */
  onChange: (minutes: string) => void;
  /** Built-in choices for this particular dropdown. */
  defaults: number[];
  placeholder?: string;
  /** Word after the number — "mins" on the schedule form, "minutes" elsewhere. */
  unit?: string;
  className?: string;
  disabled?: boolean;
}

export function IntervalSelect({
  value,
  onChange,
  defaults,
  placeholder = "Select Interval",
  unit = "mins",
  className,
  disabled,
}: IntervalSelectProps) {
  const custom = useCustomIntervals();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // Shown inside the Add dialog — a bad or duplicate value keeps it open.
  const [error, setError] = useState<string | null>(null);

  const selected = value === null || value === undefined || value === "" ? "" : String(value);

  // Built-ins + what the branch already uses + whatever is already selected (an
  // interval saved on an older schedule must still render).
  const options = useMemo(() => {
    const all = [...defaults, ...custom];
    const current = Number(selected);
    if (Number.isFinite(current) && current > 0) all.push(current);
    return Array.from(new Set(all.filter((n) => Number.isFinite(n) && n > 0))).sort(
      (a, b) => a - b
    );
  }, [defaults, custom, selected]);

  const handleChange = useCallback(
    (next: string) => {
      if (next === ADD_VALUE) {
        setDraft("");
        setError(null);
        // Let the Select finish closing before the dialog takes focus.
        setTimeout(() => setAdding(true), 0);
        return;
      }
      onChange(next);
    },
    [onChange]
  );

  const handleAddCustom = async () => {
    if (saving) return;
    const raw = draft.trim();
    const minutes = Number(raw);
    if (!raw || !Number.isInteger(minutes) || minutes <= 0 || minutes > 1440) {
      setError("Enter a whole number of minutes between 1 and 1440.");
      return;
    }
    // Already in this dropdown (a built-in choice or a value the branch has
    // saved) — say so instead of silently saving it again.
    if (options.includes(minutes)) {
      setError(`${minutes} ${unit} is already in the list.`);
      return;
    }
    setSaving(true);
    try {
      // Stored on the branch's activity setting, so it stays in the dropdown
      // for everyone — schedule saved or not.
      publish(await addIntervalOption(minutes));
      onChange(String(minutes));
      setAdding(false);
      toast({
        title: `${minutes} ${unit} added`,
        description: "Everyone in your company will see it in the dropdowns.",
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't save it. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Select value={selected} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {options.map((minutes) => (
            <SelectItem key={minutes} value={String(minutes)}>
              {minutes} {unit}
            </SelectItem>
          ))}
          <SelectItem value={ADD_VALUE} className="text-blue-600 font-medium">
            <span className="inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add interval
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={adding} onOpenChange={(open) => !saving && setAdding(open)}>
        <DialogContent className="w-[95vw] max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add interval</DialogTitle>
            <DialogDescription>
              Saved for your whole company — everyone will see it in the
              interval dropdowns.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="custom-interval-minutes">Minutes</Label>
            <Input
              id="custom-interval-minutes"
              type="number"
              min={1}
              max={1440}
              autoFocus
              value={draft}
              aria-invalid={!!error}
              aria-describedby={error ? "custom-interval-error" : undefined}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCustom();
                }
              }}
              placeholder="e.g. 45"
              className={error ? "border-red-400 focus-visible:ring-red-300" : undefined}
            />
            {error && (
              <p id="custom-interval-error" className="text-xs font-medium text-red-600">
                {error}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAdding(false)}
              disabled={saving}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCustom}
              disabled={saving}
              className="text-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default IntervalSelect;
